import { useMemo, useRef } from 'react';
import { calculateLayout, layoutMultiCampaignTasks } from '../../layoutEngine';
import { generateEdges, getEdgePath } from '../../utils/edgePaths';
import {
  buildDecisionAnswersMap,
  calculateAllFocusedTaskIds,
  filterTasksForLayout,
  filterEdgesForDisplay,
  calculateBoundsFromPositions,
} from '../utils/focusModeUtils';

// Debug flag - set to true to see focus calculations in console
const DEBUG_FOCUS = false;
const debugLog = (...args) => DEBUG_FOCUS && console.log('[FocusCalc]', ...args);

/**
 * useFocusCalculations Hook
 * 
 * Handles all the computational aspects of focus mode:
 * - Calculating which tasks are focused (primary + upstream/downstream)
 * - Computing positions for focused tasks
 * - Generating edges for focused view
 * - Calculating content bounds for minimap
 * 
 * In multi-campaign mode, each campaign's focused tasks are laid out
 * independently, then arranged side-by-side with proper spacing.
 */
export function useFocusCalculations({
  tasks,
  focusedTaskIds,
  nodePositions,
  nodeDimensions,
  containerRef,
  pan,
  zoom,
  isMultiCampaignMode = false,
  statuses = [],
}) {
  // Build decision answers map
  const decisionAnswers = useMemo(() => buildDecisionAnswersMap(tasks), [tasks]);
  
  // Get all related task IDs when in focus mode
  // Uses FOKUS logic: ALL incomplete upstream + 1 downstream
  const { allFocusedTaskIds, hiddenConnections, primaryFocusedIds } = useMemo(() => {
    const result = calculateAllFocusedTaskIds({ focusedTaskIds, tasks, decisionAnswers, statuses });
    debugLog('calculateAllFocusedTaskIds result', {
      focusedTaskIds: focusedTaskIds ? Array.from(focusedTaskIds) : null,
      allFocusedTaskIds: result.allFocusedTaskIds ? Array.from(result.allFocusedTaskIds) : null,
      primaryFocusedIds: result.primaryFocusedIds ? Array.from(result.primaryFocusedIds) : null,
      hiddenConnections: result.hiddenConnections?.length || 0,
    });
    return result;
  }, [focusedTaskIds, tasks, decisionAnswers, statuses]);
  
  // Filtered tasks for display
  const displayTasks = useMemo(() => {
    if (!allFocusedTaskIds) return tasks;
    return tasks.filter(t => allFocusedTaskIds.has(t.id));
  }, [tasks, allFocusedTaskIds]);
  
  // Tasks filtered for layout calculation
  const focusedTasksForLayout = useMemo(() => {
    if (!allFocusedTaskIds || !focusedTaskIds) return null;
    return filterTasksForLayout({ displayTasks, allFocusedTaskIds, tasks, decisionAnswers });
  }, [displayTasks, allFocusedTaskIds, tasks, decisionAnswers, focusedTaskIds]);
  
  // Store the anchor node ID and original positions when focus mode is entered
  const anchorDataRef = useRef(null);
  
  // Create a signature for focusedTaskIds to detect when it changes
  const focusedTaskIdsSignature = useMemo(() => {
    if (!focusedTaskIds) return null;
    return Array.from(focusedTaskIds).sort().join(',');
  }, [focusedTaskIds]);
  
  // Create a signature for nodeDimensions of ALL focused tasks (including upstream/downstream)
  // This ensures we recalculate positions AND edges when any node dimensions change (e.g., after view switch)
  // We must include allFocusedTaskIds, not just focusedTaskIds, because edges connect all of them
  const focusedDimensionsSignature = useMemo(() => {
    if (!allFocusedTaskIds || allFocusedTaskIds.size === 0) return null;
    // Include dimensions for ALL focused tasks (primary + upstream + downstream), sorted for consistency
    const dimEntries = Array.from(allFocusedTaskIds)
      .sort()
      .map(id => {
        const dims = nodeDimensions[id];
        // Use 'default' if no dimensions yet (will trigger recalc when they arrive)
        return dims ? `${id}:${Math.round(dims.width)}x${Math.round(dims.height)}` : `${id}:default`;
      });
    return dimEntries.join('|');
  }, [allFocusedTaskIds, nodeDimensions]);
  
  // Calculate anchor and focus positions together
  const { anchorNodeId, focusAutoPositions } = useMemo(() => {
    if (!focusedTaskIds || focusedTaskIds.size === 0 || !focusedTasksForLayout) {
      anchorDataRef.current = null;
      return { anchorNodeId: null, focusAutoPositions: null };
    }
    
    // Check if we already have anchor data for this exact set of focused tasks AND dimensions
    // The dimensions signature ensures we recalculate when node dimensions change
    const cacheSignature = `${focusedTaskIdsSignature}|${focusedDimensionsSignature}`;
    if (anchorDataRef.current && anchorDataRef.current.signature === cacheSignature) {
      return {
        anchorNodeId: anchorDataRef.current.anchorId,
        focusAutoPositions: anchorDataRef.current.positions,
      };
    }
    
    // Calculate new anchor - find node closest to screen center
    const containerRect = containerRef.current?.getBoundingClientRect();
    let anchorId;
    
    if (!containerRect) {
      anchorId = Array.from(focusedTaskIds)[0];
    } else {
      const screenCenterX = containerRect.width / 2;
      const screenCenterY = containerRect.height / 2;
      
      let closestDistance = Infinity;
      
      focusedTaskIds.forEach(taskId => {
        const pos = nodePositions[taskId];
        if (!pos) return;
        
        const dims = nodeDimensions[taskId] || { width: 290, height: 150 };
        const nodeCenterX = pos.x + dims.width / 2;
        const nodeCenterY = pos.y + dims.height / 2;
        
        const screenX = nodeCenterX * zoom + pan.x;
        const screenY = nodeCenterY * zoom + pan.y;
        
        const distance = Math.sqrt(
          Math.pow(screenX - screenCenterX, 2) + 
          Math.pow(screenY - screenCenterY, 2)
        );
        
        if (distance < closestDistance) {
          closestDistance = distance;
          anchorId = taskId;
        }
      });
    }
    
    if (!anchorId) {
      anchorId = Array.from(focusedTaskIds)[0];
    }
    
    // Snapshot original positions
    const originalPositions = { ...nodePositions };
    
    // Calculate fresh layout - handle multi-campaign mode
    let freshLayout;
    
    if (isMultiCampaignMode) {
      // Group focused tasks by campaign
      const tasksByCampaign = {};
      const campaignOrder = [];
      
      focusedTasksForLayout.forEach(task => {
        const campaignId = task.sourceCampaign?.id || 'unknown';
        if (!tasksByCampaign[campaignId]) {
          tasksByCampaign[campaignId] = [];
          campaignOrder.push(campaignId);
        }
        tasksByCampaign[campaignId].push(task);
      });
      
      // Layout each campaign independently
      const campaignPositions = {};
      campaignOrder.forEach(campaignId => {
        const campaignTasks = tasksByCampaign[campaignId];
        const layoutResult = calculateLayout(campaignTasks, nodeDimensions);
        campaignPositions[campaignId] = layoutResult.positions || layoutResult;
      });
      
      // Create tasks with their intra-campaign positions for layoutMultiCampaignTasks
      const tasksWithPositions = focusedTasksForLayout.map(task => {
        const campaignId = task.sourceCampaign?.id || 'unknown';
        const campaignPos = campaignPositions[campaignId] || {};
        return {
          ...task,
          position: campaignPos[task.id] || task.position || { x: 0, y: 0 },
        };
      });
      
      // Apply horizontal offsets to place campaigns side-by-side
      freshLayout = layoutMultiCampaignTasks(tasksWithPositions, nodeDimensions);
    } else {
      // Single campaign mode: just run normal layout
      const layoutResult = calculateLayout(focusedTasksForLayout, nodeDimensions);
      freshLayout = layoutResult.positions || layoutResult;
    }
    
    // Get anchor's original position and its position in fresh layout
    const anchorOriginalPos = originalPositions[anchorId];
    const anchorFreshPos = freshLayout[anchorId];
    
    let adjustedPositions;
    
    if (!anchorOriginalPos || !anchorFreshPos) {
      adjustedPositions = freshLayout;
    } else {
      // Calculate offset to keep anchor at its original position
      const offsetX = anchorOriginalPos.x - anchorFreshPos.x;
      const offsetY = anchorOriginalPos.y - anchorFreshPos.y;
      
      adjustedPositions = {};
      Object.keys(freshLayout).forEach(taskId => {
        adjustedPositions[taskId] = {
          x: freshLayout[taskId].x + offsetX,
          y: freshLayout[taskId].y + offsetY,
        };
      });
    }
    
    // Store for reuse - include dimensions signature to invalidate when dimensions change
    anchorDataRef.current = {
      signature: cacheSignature, // cacheSignature defined above at line 100
      anchorId,
      originalPositions,
      positions: adjustedPositions,
    };
    
    return { anchorNodeId: anchorId, focusAutoPositions: adjustedPositions };
  }, [focusedTaskIds, focusedTaskIdsSignature, focusedDimensionsSignature, focusedTasksForLayout, nodePositions, nodeDimensions, containerRef, pan, zoom, isMultiCampaignMode]);
  
  // Keep track of last focus layout for exit animation
  const lastFocusPositionsRef = useRef(null);
  const lastFocusEdgesRef = useRef(null);
  const lastFocusTasksRef = useRef(null);
  
  // Positions to use for display
  const displayPositions = useMemo(() => {
    if (focusedTaskIds && focusAutoPositions) {
      return { ...nodePositions, ...focusAutoPositions };
    }
    return nodePositions;
  }, [focusedTaskIds, focusAutoPositions, nodePositions]);
  
  // Generate edges for focused view (with pathD computed)
  const focusEdges = useMemo(() => {
    if (!focusedTaskIds || !focusAutoPositions || !focusedTasksForLayout) return null;
    const rawEdges = generateEdges(focusedTasksForLayout, focusAutoPositions, nodeDimensions);
    // Compute pathD for each edge (same as useEdgePathCache does)
    return rawEdges.map(edge => ({
      ...edge,
      pathD: getEdgePath(
        edge.fromPos,
        edge.toPos,
        edge.fromDims,
        edge.toDims,
        edge.allNodes,
        edge.edgeIndex,
        null, // No offsets for focus mode
        edge.id,
        edge.fromOffset || 0,
        edge.toOffset || 0,
        edge.fromSide,
        edge.toSide,
        null,
        edge.from,
        edge.to
      ),
    }));
  }, [focusedTaskIds, focusAutoPositions, focusedTasksForLayout, nodeDimensions]);
  
  // Update last focus layout refs when in focus mode
  if (focusedTaskIds && focusAutoPositions) {
    lastFocusPositionsRef.current = focusAutoPositions;
  }
  if (focusedTaskIds && focusEdges) {
    lastFocusEdgesRef.current = focusEdges;
  }
  if (focusedTaskIds && focusedTasksForLayout) {
    lastFocusTasksRef.current = focusedTasksForLayout;
  }
  
  // Filtered edges for display
  const displayEdges = useMemo(() => {
    return filterEdgesForDisplay({
      focusEdges,
      edges: null, // Will be passed from parent
      allFocusedTaskIds,
      decisionAnswers,
      hasFocusEdges: !!(focusedTaskIds && focusEdges),
    });
  }, [focusedTaskIds, focusEdges, allFocusedTaskIds, decisionAnswers]);
  
  // Content bounds for minimap
  const focusContentBounds = useMemo(() => {
    if (!focusedTaskIds || !focusAutoPositions) return null;
    return calculateBoundsFromPositions(focusAutoPositions, nodeDimensions);
  }, [focusedTaskIds, focusAutoPositions, nodeDimensions]);
  
  return {
    // Calculated values
    decisionAnswers,
    allFocusedTaskIds,
    hiddenConnections,
    primaryFocusedIds,
    displayTasks,
    displayPositions,
    displayEdges,
    focusAutoPositions,
    focusContentBounds,
    anchorNodeId,
    focusedTaskIdsSignature,
    
    // Refs for animation
    lastFocusPositions: lastFocusPositionsRef.current,
    lastFocusEdges: lastFocusEdgesRef.current,
    lastFocusTasks: lastFocusTasksRef.current,
  };
}

